import { CalendarDays, X } from 'lucide-react'
import DatePicker from './DatePicker'
import { kit } from './strings'
import './DateRangeField.css'

/**
 * From/to as one control.
 *
 * Four screens hand-roll this out of two text fields and a placeholder; the two
 * halves share a single shell here so the range reads as one value. Native date
 * inputs keep the picker the OS provides, and the values stay LTR inside the RTL
 * layout because an ISO date is not Arabic text.
 *
 * An empty native date input prints the OS mask — `mm/dd/yyyy` on an Arabic-first
 * screen — and draws its own calendar glyph, so the pair used to render three
 * calendars and two US date masks. Empty slots now show the Arabic label instead
 * and the native indicators are hidden; the whole slot opens the picker, which is
 * a far larger target than the 16px glyph it replaces.
 *
 * @param {{from: string, to: string}} value   ISO `YYYY-MM-DD`, empty for unset
 * @param {(value: {from: string, to: string}) => void} onChange
 */
export default function DateRangeField({ value, onChange, labels = {}, id }) {
  const set = (key) => (next) => onChange({ ...value, [key]: next })
  const filled = Boolean(value.from || value.to)

  /* Each half is a DatePicker in its `bare` variant: the shell around both is
     what makes the pair read as ONE value, so the halves must not each carry
     their own. The bounds keep the range ordered — `from` cannot pass `to`. */
  const slot = (key) => (
    <DatePicker
      variant="bare"
      value={value[key]}
      onChange={set(key)}
      placeholder={labels[key]}
      max={key === 'from' ? value.to || undefined : undefined}
      min={key === 'to' ? value.from || undefined : undefined}
      aria-label={labels[key]}
    />
  )

  return (
    <div className={`date-range${filled ? ' is-filled' : ''}`} id={id}>
      <CalendarDays className="date-range__icon" size={16} strokeWidth={1.75} aria-hidden="true" />
      {slot('from')}
      <span className="date-range__dash" aria-hidden="true" />
      {slot('to')}
      {filled && (
        <button
          type="button"
          className="date-range__clear"
          onClick={() => onChange({ from: '', to: '' })}
          aria-label={kit.clearDate}
        >
          <X size={13} strokeWidth={2.5} />
        </button>
      )}
    </div>
  )
}
